import React, { useMemo } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { Button } from '../../../../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../components/ui/popover';
import { Calendar } from '../../../../components/ui/calendar';
import { cn } from 'utils/shadcn';
import { format } from 'date-fns';
import { useAnalyticsContext } from '../analytics-context';
import { DateRange } from 'react-day-picker';

const dateFilters = [
  { label: 'Today', value: 'Today' },
  { label: 'Yesterday', value: 'Yesterday' },
  { label: 'This week', value: 'This week' },
  { label: 'Last week', value: 'Last week' },
  { label: 'Last 7 days', value: 'Last 7 days' },
  { label: 'Last 14 days', value: 'Last 14 days' },
  { label: 'Last 28 days', value: 'Last 28 days' },
  { label: 'Last 30 days', value: 'Last 30 days' },
  { label: 'Last 60 days', value: 'Last 60 days' },
  { label: 'Last 90 days', value: 'Last 90 days' },
  { label: 'Last 12 Month', value: 'Last 12 Month' },
  { label: 'This Year', value: 'This Year' },
  { label: 'Last Year', value: 'Last Year' },
  { label: 'Custom Range', value: 'custom' },
];

export const AnalyticsFilterBar: React.FC = () => {
  const {
    selectedFilter,
    setSelectedFilter,
    dateRange,
    setDateRange,
  } = useAnalyticsContext();

  const displayLabel = useMemo(() => {
    if (selectedFilter !== 'custom') {
      return dateFilters.find(option => option.value === selectedFilter)
        ?.label;
    }

    if (dateRange?.from && dateRange?.to) {
      return `${format(dateRange.from, 'LLL dd, y')} - ${format(
        dateRange.to,
        'LLL dd, y',
      )}`;
    }

    return 'Select a date range';
  }, [selectedFilter, dateRange]);

  const handleFilterChange = (value: string) => {
    setSelectedFilter(value);
    if (value !== 'custom') {
      // Reset range to avoid stale custom selections
      setDateRange({ from: undefined, to: undefined });
    }
  };

  const handleRangeSelect = (range: DateRange | undefined) => {
    setDateRange(range || { from: undefined, to: undefined });
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card px-4 py-3 shadow-sm">
      <div>
        <h2 className="text-base font-semibold text-foreground">
          Analytics Overview
        </h2>
        <p className="text-sm text-muted-foreground">
          All charts update automatically when you change the time range.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={selectedFilter} onValueChange={handleFilterChange}>
          <SelectTrigger className="w-[200px] text-sm font-medium">
            <SelectValue placeholder="Select period" />
          </SelectTrigger>
          <SelectContent>
            {dateFilters.map(filter => (
              <SelectItem key={filter.value} value={filter.value}>
                {filter.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedFilter === 'custom' && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-[260px] justify-start text-left font-normal text-sm',
                  !dateRange?.from && 'text-muted-foreground',
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {displayLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={handleRangeSelect}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
};

